import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Divider } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { push } from "connected-react-router";
import { Formik, Form } from "formik";
import CourseSettingsGlobal from "./CourseSettingsGlobal";
import CourseSettingsDefault from "./CourseSettingsDefault";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import isEqual from "lodash/isEqual";
import {
  fetchCourseSettings,
  updateCourseSettings
} from "../../../../redux/actions/contentCoursesActions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { PropTypes } from "prop-types";
import { buildCourseSetPrereqsRoute } from "../../../../routes/AdminRoutes";
import WarningModal from "./SaveCourseSettingsWarningModal";
import { contentOwners, courseTypes } from "../../../../constants";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import { enqueueSuccessSnackbar } from "../../../../redux/actions/snackbarActions";
import { camelCase } from "lodash";

const selector = makeGetEntities();

const getCourseSettingsSchema = t => {
  return Yup.object().shape({
    courseTitle: Yup.string().required(t("validationMessages.fieldRequired")),
    durationHours: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber"))
      .required(t("validationMessages.fieldRequired")),
    timeToComplete: Yup.number()
      .typeError(t("validationMessages.validNumber"))
      .min(0, t("validationMessages.validNumber")),
    description: Yup.string().max(
      800,
      t("validationMessages.cannotExceedLength", {
        type: t("manageCourses.addCourseDescriptionField"),
        maxLength: 800
      })
    ),
    passingScore: Yup.mixed().test(
      "passingScoreRequired",
      t("validationMessages.validNumber"),
      function() {
        return (
          this.parent.recordScore === "no" ||
          (this.parent.recordScore === "yes" &&
            Number.isInteger(+this.parent.passingScore) &&
            this.parent.passingScore > 0)
        );
      }
    ),
    recurringInterval: Yup.mixed().test(
      "recurringIntervalRequired",
      t("validationMessages.validNumber"),
      function() {
        return (
          this.parent.recurringEvent === "no" ||
          (this.parent.recurringEvent === "yes" &&
            Number.isInteger(+this.parent.recurringInterval) &&
            this.parent.recurringInterval > 0)
        );
      }
    )
  });
};

const isScoreChanged = (newValues, oldValues) => {
  return (
    newValues &&
    oldValues &&
    ((newValues.recordScore === "yes" &&
      oldValues.passingScore !== newValues.passingScore) ||
      oldValues.recordScore !== newValues.recordScore)
  );
};

const isTimeChanged = (newValues, oldValues) => {
  return (
    newValues &&
    oldValues &&
    (oldValues.timeToComplete !== newValues.timeToComplete ||
      (oldValues.timeToCompleteUnit &&
        oldValues.timeToCompleteUnit !== newValues.timeToCompleteUnit) ||
      oldValues.recurringEvent !== newValues.recurringEvent ||
      oldValues.recurringInterval !== newValues.recurringInterval ||
      (oldValues.recurringIntervalUnit &&
        oldValues.recurringIntervalUnit !== newValues.recurringIntervalUnit))
  );
};

// TODO: add redux implementation to fetch the course settings if not already present in the redux store
const CourseSettings = ({
  match,
  course: {
    contentOwner,
    clientCourse,
    customGraceHillCourse,
    isMasterCustomer,
    type,
    id
  }
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const courseId = match.params.courseId || id;

  const [warnings, setWarnings] = useState({});
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [courseSettingsData, setCourseSettingsData] = useState(null);
  const [
    displayInLibraryWarningModalOpen,
    setDisplayInLibraryWarningModalOpen
  ] = useState(false);
  const [
    displayInLibraryInitialLabel,
    setDisplayInLibraryInitialLabel
  ] = useState("");
  const { courseSettings, loading } = useSelector(
    ({ contentCoursesState }) => ({
      courseSettings: selector(contentCoursesState, {
        type: "courseSettings",
        id: courseId
      }),
      loading: contentCoursesState.isFetchingCourseSettings
    })
  );

  useEffect(() => {
    dispatch(fetchCourseSettings(courseId));
  }, []);

  useEffect(() => {
    setCourseSettingsData(courseSettings);
    setDisplayInLibraryInitialLabel(courseSettings?.displayInLibrary);
  }, [courseSettings]);

  const isIltCourse = type === courseTypes.ilt;

  const customContent = contentOwner === contentOwners.custom;
  const masterCourse = contentOwner === contentOwners.master;

  const handleSetPrerequisitesClick = () => {
    dispatch(push(buildCourseSetPrereqsRoute(courseId)));
  };

  const handleSettingsUpdate = values => {
    const settingsDataCopy = { ...courseSettingsData };
    Object.keys(values).forEach(value => {
      if (settingsDataCopy[value] !== values[value]) {
        settingsDataCopy[value] = values[value];
      }
    });
    if (!isEqual(settingsDataCopy, courseSettingsData)) {
      setCourseSettingsData(null);
      setTimeout(() => {
        setCourseSettingsData(settingsDataCopy);
        dispatch(updateCourseSettings(courseId, values)).then(response => {
          if (response.error || response.errors) {
            dispatch(
              enqueueErrorSnackbar({
                message: response?.error
                  ? t(
                      "manageCourses.displayInLibraryMessage" +
                        camelCase(response.error)
                    )
                  : "Something went wrong"
              })
            );
          } else {
            const customersWithErrors =
              response.data?.attributes.customersWithErrors;
            dispatch(
              enqueueSuccessSnackbar({
                message: customersWithErrors
                  ? t("manageCourses.displayInLibraryMessage.successWithNote", {
                      customersWithError: customersWithErrors
                    })
                  : t("manageCourses.displayInLibraryMessage.success")
              })
            );
          }
        });
      }, 2000);
    }
  };

  useEffect(() => {
    if (!formValues) {
      return;
    }
    const scoreChanged = isScoreChanged(formValues, courseSettingsData);
    const timeChanged = isTimeChanged(formValues, courseSettingsData);
    const showEquivalentChanged =
      courseSettingsData.allowEquivalents !== formValues.allowEquivalents;
    if (scoreChanged || timeChanged || showEquivalentChanged) {
      setWarnings({
        scoreChanged: scoreChanged,
        timeChanged: timeChanged,
        showEquivalentChanged: showEquivalentChanged
      });
      setShowWarningMessage(true);
    } else {
      handleSettingsUpdate(formValues);
    }
  }, [formValues]);

  const handleModalClose = () => {
    setShowWarningMessage(false);
  };

  const handleModalSubmit = () => {
    setShowWarningMessage(false);
    handleSettingsUpdate(formValues);
  };

  const isDisabled = () => {
    return (
      !(contentOwner === contentOwners.custom) ||
      (courseSettingsData.isActive === "yes" &&
        (courseSettings.assignments.length > 0 ||
          courseSettings.enrolledAssignments.length > 0))
    );
  };

  return !loading && courseSettingsData ? (
    <React.Fragment>
      <WarningModal
        open={showWarningMessage}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        warningMessages={warnings}
        courseType={type.toLowerCase()}
      />
      <Formik
        validationSchema={getCourseSettingsSchema(t)}
        initialValues={{
          showEquivalentToggle:
            courseSettingsData.showEquivalentToggle || false,
          courseTitle: courseSettingsData.courseTitle || "",
          description: courseSettingsData.description || "",
          tags: courseSettingsData.tags ? courseSettingsData.tags : [],
          durationHours: courseSettingsData.durationHours || 0,
          durationMinutes: courseSettingsData.durationMinutes || 0,
          includeCertificate: courseSettingsData.includeCertificate || "no",
          // Display in Library should only be added to form for non-ILT courses
          ...(!isIltCourse && {
            displayInLibrary: courseSettingsData.displayInLibrary || "no"
          }),
          displayInStorefront: courseSettingsData.displayInStorefront || "no",
          isCredential: courseSettingsData.isCredential || "no",
          isActive: courseSettingsData.isActive || "no",
          timeToComplete: courseSettingsData.timeToComplete || 0,
          timeToCompleteUnit: courseSettingsData.timeToCompleteUnit || "days",
          recordScore: courseSettingsData.recordScore || "no",
          passingScore: courseSettingsData.passingScore || 70,
          disableCustomScore: courseSettingsData.disableCustomScore || false,
          showDisableCustomScore:
            courseSettingsData.showDisableCustomScore || false,
          // Paper Based Test should only be added to form for ILT courses
          ...(isIltCourse && {
            paperBasedTest: courseSettingsData.paperBasedTest || "no"
          }),
          recurringEvent: courseSettingsData.recurringEvent || "no",
          allowEquivalents: courseSettingsData.allowEquivalents || "no",
          recurringInterval: courseSettingsData.recurringInterval || 0,
          recurringIntervalUnit:
            courseSettingsData.recurringIntervalUnit || "days",
          courseImage: courseSettingsData.courseImage || ""
        }}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setFormValues(values);
          setSubmitting(false);
        }}
      >
        {({ submitForm, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <CourseSettingsGlobal
                  id={id}
                  values={values}
                  setFieldValue={setFieldValue}
                  isDisabled={isDisabled}
                  customContent={customContent}
                  masterCourse={masterCourse}
                  courseType={type}
                  assignments={courseSettings.assignments || []}
                  enrolledAssignments={courseSettings.enrolledAssignments || []}
                  submitForm={submitForm}
                  displayInLibraryInitialLabel={displayInLibraryInitialLabel}
                  displayInLibraryWarningModalOpen={
                    displayInLibraryWarningModalOpen
                  }
                  setDisplayInLibraryWarningModalOpen={
                    setDisplayInLibraryWarningModalOpen
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CourseSettingsDefault
                  values={values}
                  courseType={type}
                  graceHillContent={contentOwner === contentOwners.graceHill}
                />
              </Grid>
            </Grid>
            <Box mt={5}>
              <Divider />
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              mt={5}
              flexDirection={{ xs: "column", sm: "row" }}
              height={{ xs: 100, sm: "unset" }}
            >
              <Button
                startIcon={<ListAltIcon />}
                color="primary"
                onClick={handleSetPrerequisitesClick}
              >
                {t("manageCourses.courseSettingsSetPrerequisitesBtn")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (
                    isMasterCustomer &&
                    ((customContent && clientCourse) ||
                      (masterCourse && !customGraceHillCourse))
                  ) {
                    if (
                      values.displayInLibrary != displayInLibraryInitialLabel
                    ) {
                      setDisplayInLibraryWarningModalOpen(true);
                    } else {
                      submitForm();
                    }
                  } else {
                    submitForm();
                  }
                }}
                data-qa="course-settings-save-btn"
              >
                {t("manageCourses.courseSettingsSaveBtn")}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  ) : (
    <LoadingSpinner />
  );
};

export default CourseSettings;

CourseSettings.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      courseId: PropTypes.string
    })
  })
};
